// Campaign promo codes: a publisher-shared link (https://<domain>/?promo=SAVE20)
// or the showPaywall/openCheckout { promoCode } option pre-applies a code at
// checkout. This module owns the URL-param side: capture at bootstrap and
// again at every mount (last-click), the sessionStorage stash (internal
// navigation drops the query string), and the deferred URL strip.
//
// The param is NOT stripped at capture: a bare `promo` key is shared
// namespace, and a foreign value (the publisher's own plugin/analytics) must
// be left untouched for whatever script owns it. The strip happens on first
// domain-own classification (see classifyCampaignCode) — anything but
// not_found means the code is (or was) ours, so the stash is the source of
// truth from then on and the URL can be cleaned.

import { logError, logWarn } from '@/sdkConsole';
import type { CampaignPromo, CampaignPromoSource } from '@/types';

const PARAM = 'promo';
const STORAGE_KEY = 'ezsm_campaign_promo';

export type { CampaignPromo, CampaignPromoSource };

// A ?promo= this document already classified as not_found (foreign param).
// Recapture-on-mount must not revive it; a different live param still last-clicks.
let ignoredUrlPromo = '';

// resolveCampaignPromo picks the effective campaign code for a mount: an
// explicit API promoCode wins over the URL stash (explicit opts always win,
// matching mountPaywall's parked-selection rule). Recaptures a live ?promo=
// first so a param that arrived after bootstrap still applies.
export function resolveCampaignPromo(apiCode?: string): CampaignPromo | null {
  captureCampaignPromo();
  const code = apiCode?.trim().toUpperCase() || '';
  if (code) {
    return { code, source: 'api' };
  }
  const stashed = getStashedCampaignPromo();
  if (stashed) {
    return stashed;
  }
  // Storage unavailable: still honor a live ?promo= on this page.
  const fromUrl = promoFromSearch();
  if (!fromUrl || fromUrl === ignoredUrlPromo) {
    return null;
  }
  return { code: fromUrl, source: 'url' };
}

function promoFromSearch(search?: string): string {
  const raw = new URLSearchParams(search ?? window.location.search).get(PARAM);
  return raw?.trim().toUpperCase() || '';
}

// captureCampaignPromo stashes a ?promo= param (write-if-changed: a newer
// campaign link's code wins, matching last-click attribution). Runs at
// bootstrap and again from resolveCampaignPromo on every mount. The URL is
// left untouched.
export function captureCampaignPromo(search?: string): void {
  const code = promoFromSearch(search);
  if (!code || code === ignoredUrlPromo) {
    return;
  }
  try {
    if (sessionStorage.getItem(STORAGE_KEY) !== code) {
      sessionStorage.setItem(STORAGE_KEY, code);
    }
  } catch {
    // Storage unavailable (private mode / disabled): the code still works on
    // the landing page via the param itself once the paywall opens there.
  }
}

// getStashedCampaignPromo returns the URL-stashed code, if any.
export function getStashedCampaignPromo(): CampaignPromo | null {
  try {
    const code = sessionStorage.getItem(STORAGE_KEY);
    return code ? { code, source: 'url' } : null;
  } catch {
    return null;
  }
}

export function clearStashedCampaignPromo(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// stripCampaignParam removes the promo param from the address bar, leaving
// every other param and the hash exactly as they were.
function stripCampaignParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(PARAM)) {
    return;
  }
  url.searchParams.delete(PARAM);
  window.history.replaceState(null, '', url.toString());
}

// codeDeadReasons are failures no price selection can fix — after the error
// surfaces once at apply, the stash clears so it doesn't nag on every reopen.
const codeDeadReasons = new Set(['expired', 'inactive', 'redemption_limit']);

// urlSilentApplyReasons are apply failures a url-stashed code never surfaces
// inline: not_found (foreign param) plus the scope mismatches — the code is
// alive, it just doesn't fit this purchase, and the picker never showed a
// discount for it, so there is no broken promise to explain. Code-dead
// reasons stay loud: the campaign promised a discount that no longer exists.
const urlSilentApplyReasons = new Set(['not_found', 'product_not_eligible', 'minimum_not_met', 'currency_not_eligible']);

// campaignApplyErrorIsSilent is the payment-step half of the provenance ×
// reason matrix: whether an auto-apply failure renders an inline error.
export function campaignApplyErrorIsSilent(source: CampaignPromoSource, reason: string): boolean {
  return source === 'url' && urlSilentApplyReasons.has(reason);
}

// classifyCampaignCode applies the provenance × reason matrix's stash, URL,
// and console side effects when a campaign code is classified — at preview
// (the config load's promo block) or at apply (the payment step's validate).
// reason is null on success. The picker/payment UI decides what to render;
// this only owns the side effects.
export function classifyCampaignCode(source: CampaignPromoSource, reason: string | null, stage: 'preview' | 'apply'): void {
  if (source === 'api') {
    if (reason) {
      logError(`campaign promoCode failed: ${reason}`);
    }
    return;
  }
  if (reason === 'not_found') {
    // Foreign ?promo= value: drop the stash, leave the URL for its owner.
    ignoredUrlPromo = getStashedCampaignPromo()?.code || promoFromSearch();
    clearStashedCampaignPromo();
    logWarn('ignoring unrecognized promo param');
    return;
  }
  // Domain-own (valid or once-valid): the stash is the source of truth now.
  stripCampaignParam();
  if (!reason) {
    return;
  }
  logWarn(`campaign promo failed: ${reason}`);
  if (stage === 'apply' && codeDeadReasons.has(reason)) {
    clearStashedCampaignPromo();
  }
}
